/**
 * O tradutor: snapshot -> lista de instrucoes de desenho.
 *
 * E o unico lugar do sistema que decide alguma coisa sobre aparencia. Depois
 * daqui so existe pintura: coordenadas em pontos, cor em RGB, opacidade em
 * numero, texto ja quebrado em linhas.
 *
 * Funcao PURA. Mesma entrada, mesma saida, em qualquer runtime. Sem
 * `Date.now()`, sem `Math.random()`, sem `Intl`, sem I/O.
 *
 * Toda primitiva carrega `src` — o elemento que a gerou. E o que permite a
 * pergunta mecanica "o elemento X esta ligado; ele produziu alguma coisa?",
 * que um teste faz para os 14 elementos em todos os fixtures.
 */

import {
  type Primitive, type Diagnostic, type DiagnosticCode, type DiagnosticSeverity,
  type ElementId, type ResolvedCertificate, type PageSize, type AssetManifest,
  type CertificateSnapshot, type CertificateData, type AssetId, type FontRef,
  ELEMENT_ORDER,
} from '../../types.js';
import { normalizeConfig } from '../../schema/normalize.js';
import type { V2Config, V2Element, V2Style } from '../../schema/v2.js';
import { FONT_SIZE_PX } from '../../schema/v2.js';
import { buildPalette, resolveInk, resolveAccentInk, mix, type Palette, type Ink } from './theme.js';
import {
  medirLinha, larguraDe, quebrarEmLinhas, ajustarAoEspaco, sanitizarParaFonte,
  formatarDataEvento, formatarCargaHoraria, partesNoFuso, hora as formatarHora,
  aplicarCaixa, offsetConhecido,
} from './text.js';
import {
  q, toPt, pxParaPt, alturaLinha, posicionarBloco, larguraUtil,
  colunasCentradas, sequenciaCentrada, ancoraX, offsetBaseline,
} from './layout.js';

// ─── Coletor ────────────────────────────────────────────────────────────────

class Saida {
  readonly primitivas: Primitive[] = [];
  private readonly diags: Diagnostic[] = [];

  push(p: Primitive): void {
    this.primitivas.push(p);
  }

  diag(
    code: DiagnosticCode,
    severity: DiagnosticSeverity,
    message: string,
    element?: ElementId,
  ): void {
    this.diags.push(element ? { code, severity, message, element } : { code, severity, message });
  }

  /**
   * Ja existe diagnostico deste codigo para este elemento?
   *
   * Serve so a rede de seguranca do `resolve`. A rede existe para pegar o ramo
   * que ESQUECEU de avisar; quando o ramo avisou, ela nao tem o que acrescentar
   * e emitir de novo produz duas linhas para um problema so — no editor isso
   * aparece como dois avisos identicos, e o produtor conta dois defeitos onde
   * ha um.
   *
   * A rede continua intacta: sem diagnostico especifico, ela dispara.
   */
  jaAvisou(code: DiagnosticCode, element: ElementId): boolean {
    return this.diags.some((d) => d.code === code && d.element === element);
  }

  /** Ordem estavel: diagnostico entra em golden test. */
  diagnosticos(): Diagnostic[] {
    const rank: Record<DiagnosticSeverity, number> = { error: 0, warn: 1, info: 2 };
    return [...this.diags].sort(
      (a, b) =>
        rank[a.severity] - rank[b.severity] ||
        a.code.localeCompare(b.code) ||
        (a.element ?? '').localeCompare(b.element ?? '') ||
        a.message.localeCompare(b.message),
    );
  }
}

// ─── Fonte e corpo ──────────────────────────────────────────────────────────

/**
 * Sora e a fonte de display; Inter e a de texto.
 *
 * Sora so no titulo e no nome do participante — os dois blocos que o
 * certificado usa como ancora visual. O resto em Inter, que tem melhor
 * legibilidade em corpo pequeno.
 */
function escolherFonte(id: ElementId, style: V2Style): FontRef {
  const display = id === 'title' || id === 'participantName';
  if (display) return 'sora-700';
  return style.fontWeight === 'bold' || style.fontWeight === 'semibold'
    ? 'inter-600'
    : 'inter-400';
}

function corpoPt(style: V2Style): number {
  return pxParaPt(FONT_SIZE_PX[style.fontSize]);
}

// ─── Texto de cada elemento ─────────────────────────────────────────────────

function primeiroNome(nome: string): string {
  const p = nome.trim().split(' ').filter(Boolean);
  return p.length > 0 ? p[0] : nome;
}

function textoDe(
  id: ElementId,
  cfg: V2Config,
  d: CertificateData,
  tz: string,
): string {
  switch (id) {
    case 'title':
      return cfg.text.title;
    case 'introText':
      return cfg.text.introText;
    case 'participantName':
      return cfg.fields.showParticipantLastName
        ? d.attendeeName
        : primeiroNome(d.attendeeName);
    case 'cpf':
      return d.attendeeDocumentMasked ? `CPF: ${d.attendeeDocumentMasked}` : '';
    case 'participationText':
      return cfg.text.participationText;
    case 'eventName':
      return d.eventTitle;
    case 'conclusionText':
      return cfg.text.conclusionText;
    case 'validationLabel':
      return 'VALIDAÇÃO DIGITAL';
    case 'validationUrl':
      return d.verificationUrl.replace(/^https?:\/\//, '');
    case 'certificateCode':
      return d.certificateCode;
    // Compostos: nao tem texto proprio.
    case 'titleDivider':
    case 'nameUnderline':
    case 'metadataRow':
    case 'signatures':
      return '';
  }
}

/** Gate de `fields`. Elemento ligado mas com o campo desmarcado nao desenha. */
function permitidoPorFields(id: ElementId, cfg: V2Config): boolean {
  switch (id) {
    case 'participantName':
      return cfg.fields.showParticipantName;
    case 'cpf':
      return cfg.fields.showDocument;
    case 'eventName':
      return cfg.fields.showEventName;
    case 'signatures':
      return cfg.fields.showSigners;
    default:
      return true;
  }
}

// ─── Blocos de texto ────────────────────────────────────────────────────────

/** Quantas linhas um elemento pode ocupar antes de encolher. */
function maxLinhas(id: ElementId): number {
  if (id === 'conclusionText') return 4;
  if (id === 'eventName' || id === 'participantName') return 2;
  return 3;
}

function desenharTexto(
  out: Saida,
  id: ElementId,
  el: V2Element,
  texto: string,
  ink: Ink,
  page: PageSize,
): void {
  const font = escolherFonte(id, el.style);
  const tamanho = corpoPt(el.style);
  const ls = el.style.letterSpacing;

  const limpo = sanitizarParaFonte(aplicarCaixa(texto, el.style.uppercase), font);
  if (limpo.substituidos.length > 0) {
    out.diag('GLYPH_UNSUPPORTED', 'warn',
      `${limpo.substituidos.length} caractere(s) sem desenho na fonte foram substituídos.`, id);
  }

  const largura = larguraUtil(el.width, page);
  const ajuste = ajustarAoEspaco(limpo.texto, font, tamanho, largura, maxLinhas(id), ls);

  if (ajuste.linhas.length === 0) {
    out.diag('ELEMENT_EMPTY', 'warn', `"${id}" está ligado mas não tem texto.`, id);
    return;
  }
  if (ajuste.encolheu) {
    out.diag('TEXT_SHRUNK', 'info',
      `Texto de "${id}" reduzido para caber na largura definida.`, id);
  }
  if (ajuste.truncou) {
    out.diag('TEXT_TRUNCATED', 'warn',
      `Texto de "${id}" não coube e foi cortado.`, id);
  }

  const larguras = ajuste.linhas.map((l) => larguraDe(l, font, ajuste.tamanhoPt, ls));
  const posicoes = posicionarBloco(
    ajuste.linhas, larguras,
    toPt(el.x, page.width), toPt(el.y, page.height),
    el.style.textAlign, font, ajuste.tamanhoPt, page,
  );

  for (const p of posicoes) {
    out.push({
      k: 'glyphs', text: p.texto, font, size: ajuste.tamanhoPt,
      x: p.x, y: p.y, charSpacing: q(ls * ajuste.tamanhoPt),
      fill: ink.fill, opacity: ink.opacity, src: id,
    });
  }
}

// ─── Compostos ──────────────────────────────────────────────────────────────

/** Divisor do titulo: traço, losango, traço. Sem trigonometria. */
function desenharDivisor(out: Saida, el: V2Element, palette: Palette, page: PageSize): void {
  const ink = resolveAccentInk(el.style, palette);
  const cx = toPt(el.x, page.width);
  const cy = toPt(el.y, page.height);
  const meia = 60, gap = 8, losango = 4;

  out.push({ k: 'line', x1: q(cx - meia), y1: cy, x2: q(cx - gap), y2: cy,
    stroke: ink.fill, w: 0.6, opacity: ink.opacity, src: 'titleDivider' });

  out.push({ k: 'poly',
    pts: [[cx, q(cy - losango)], [q(cx + losango), cy], [cx, q(cy + losango)], [q(cx - losango), cy]],
    fill: ink.fill, opacity: ink.opacity, src: 'titleDivider' });

  out.push({ k: 'line', x1: q(cx + gap), y1: cy, x2: q(cx + meia), y2: cy,
    stroke: ink.fill, w: 0.6, opacity: ink.opacity, src: 'titleDivider' });
}

/**
 * Sublinhado do nome: 48 retangulos com cor interpolada, esmaecendo nas pontas.
 *
 * N fixo em 48, nunca configuravel: o numero entra no fingerprint, e deixar o
 * produtor mexer criaria goldens que mudam sozinhos.
 */
const SEGMENTOS_SUBLINHADO = 48;

function desenharSublinhado(out: Saida, el: V2Element, palette: Palette, page: PageSize): void {
  const ink = resolveAccentInk(el.style, palette);
  const cx = toPt(el.x, page.width);
  const cy = toPt(el.y, page.height);
  const largura = el.width !== null ? el.width * page.width : 165;
  const passo = largura / SEGMENTOS_SUBLINHADO;
  const inicio = cx - largura / 2;

  for (let i = 0; i < SEGMENTOS_SUBLINHADO; i++) {
    // Triangular: 0 nas pontas, 1 no meio. Só soma e divisão.
    const meio = (SEGMENTOS_SUBLINHADO - 1) / 2;
    const dist = i <= meio ? i / meio : (SEGMENTOS_SUBLINHADO - 1 - i) / meio;
    out.push({
      k: 'rect', x: q(inicio + i * passo), y: q(cy), w: q(passo + 0.2), h: 0.8,
      fill: mix(palette.paper, ink.fill, dist),
      opacity: q(ink.opacity * dist), src: 'nameUnderline',
    });
  }
}

/** Linha de dados: rótulo pequeno em cima, valor embaixo, colunas sem colidir. */
function desenharDados(
  out: Saida, el: V2Element, cfg: V2Config, d: CertificateData,
  tz: string, palette: Palette, page: PageSize,
): void {
  const pares: { rotulo: string; valor: string }[] = [];

  if (cfg.fields.showEventDate) {
    const data = formatarDataEvento(d.eventStartsAt, d.eventEndsAt, cfg.fields.eventDateMode, tz);
    if (data) pares.push({ rotulo: 'DATA', valor: data });
  }
  if (cfg.fields.showEventTime && d.eventStartsAt) {
    const p = partesNoFuso(d.eventStartsAt, tz);
    if (p) pares.push({ rotulo: 'HORÁRIO', valor: formatarHora(p) });
  }
  if (cfg.fields.showEventLocation && d.venueName) {
    pares.push({ rotulo: 'LOCAL', valor: d.venueName });
  }
  if (cfg.fields.showWorkload && d.workloadHours !== null) {
    pares.push({ rotulo: 'CARGA HORÁRIA', valor: formatarCargaHoraria(d.workloadHours) });
  }

  if (pares.length === 0) {
    out.diag('ELEMENT_EMPTY', 'info',
      'A linha de dados do evento está ligada, mas nenhum dado foi marcado para exibir.',
      'metadataRow');
    return;
  }

  const ink = resolveInk(el.style, palette);
  const fonteRot: FontRef = 'inter-600';
  const fonteVal: FontRef = 'inter-400';
  const tamRot = 5.5, tamVal = corpoPt(el.style);

  // Cada coluna ocupa a largura do seu conteúdo — é o que impede a
  // sobreposição que o renderizador atual produz com 120pt fixos.
  const larguras = pares.map((p) =>
    Math.max(larguraDe(p.rotulo, fonteRot, tamRot, 0.08), larguraDe(p.valor, fonteVal, tamVal)),
  );
  const cols = sequenciaCentrada(toPt(el.x, page.width), larguras, 26);
  const cy = toPt(el.y, page.height);

  pares.forEach((par, i) => {
    const col = cols[i];
    const wRot = larguraDe(par.rotulo, fonteRot, tamRot, 0.08);
    const wVal = larguraDe(par.valor, fonteVal, tamVal);

    out.push({
      k: 'glyphs', text: par.rotulo, font: fonteRot, size: tamRot,
      x: q(col.centro - wRot / 2), y: q(cy - 3),
      charSpacing: q(0.08 * tamRot), fill: palette.muted,
      opacity: q(ink.opacity * 0.9), src: 'metadataRow',
    });
    out.push({
      k: 'glyphs', text: par.valor, font: fonteVal, size: tamVal,
      x: q(col.centro - wVal / 2), y: q(cy + offsetBaseline(fonteVal, tamVal)),
      charSpacing: 0, fill: ink.fill, opacity: ink.opacity, src: 'metadataRow',
    });

    if (i < pares.length - 1) {
      const meio = q((col.fim + cols[i + 1].inicio) / 2);
      out.push({
        k: 'line', x1: meio, y1: q(cy - 4), x2: meio, y2: q(cy + 6),
        stroke: palette.muted, w: 0.4, opacity: q(ink.opacity * 0.5), src: 'metadataRow',
      });
    }
  });
}

/** Assinaturas: imagem (ou linha), nome, cargo. */
const LARGURA_COLUNA_ASSINATURA = 150;
const ALTURA_ASSINATURA = 34;

function desenharAssinaturas(
  out: Saida, el: V2Element, cfg: V2Config,
  manifest: AssetManifest, palette: Palette, page: PageSize,
  usados: Set<AssetId>,
): void {
  if (cfg.signers.length === 0) {
    out.diag('ELEMENT_EMPTY', 'info',
      'O bloco de assinaturas está ligado, mas nenhum signatário foi cadastrado.',
      'signatures');
    return;
  }

  const ink = resolveInk(el.style, palette);
  const cy = toPt(el.y, page.height);
  const centros = colunasCentradas(
    toPt(el.x, page.width), cfg.signers.length, LARGURA_COLUNA_ASSINATURA,
  );

  cfg.signers.forEach((s, i) => {
    const cx = centros[i];
    const asset = s.signatureUrl
      ? manifest.assets.find((a) => a.role === 'signature' && a.src === s.signatureUrl)
      : undefined;

    if (asset) {
      /*
       * Escala preservando proporcao, limitada pela altura.
       *
       * `asset.h` PODE VIR ZERO. O manifesto que o preview do editor monta nao
       * conhece as dimensoes do arquivo — o navegador so as sabe depois de
       * carregar a imagem. Sem esta guarda, `ALTURA / 0` da Infinity, `0 *
       * Infinity` da NaN, e a primitiva sai com `x` e `w` NaN: a assinatura
       * simplesmente nao aparece, sem erro nenhum no console.
       *
       * A proporcao de reserva e 3:1, que e a forma de uma assinatura digital
       * tipica. E um chute, e por isso ele so vale enquanto ninguem informou a
       * medida de verdade — na emissao os assets carregam `w`/`h` reais.
       */
      const proporcao = asset.w > 0 && asset.h > 0 ? asset.w / asset.h : 3;
      const w = q(Math.min(ALTURA_ASSINATURA * proporcao, LARGURA_COLUNA_ASSINATURA - 20));
      const h = q(ALTURA_ASSINATURA);
      out.push({
        k: 'image', x: q(cx - w / 2), y: q(cy - h - 2), w, h,
        asset: asset.id, opacity: ink.opacity, src: 'signatures',
      });
      usados.add(asset.id);
    } else {
      if (s.signatureUrl) {
        out.diag('ASSET_MISSING', 'warn',
          `A imagem de assinatura de "${s.name}" não foi encontrada; usando linha.`, 'signatures');
      }
      // Sem imagem: linha de assinatura. Com imagem, a linha NÃO é desenhada —
      // o renderizador atual desenha as duas, e o traço corta a assinatura.
      out.push({
        k: 'line', x1: q(cx - 50), y1: cy, x2: q(cx + 50), y2: cy,
        stroke: ink.fill, w: 0.5, opacity: q(ink.opacity * 0.5), src: 'signatures',
      });
    }

    const wNome = larguraDe(s.name, 'inter-600', 8);
    out.push({
      k: 'glyphs', text: s.name, font: 'inter-600', size: 8,
      x: q(cx - wNome / 2), y: q(cy + 9), charSpacing: 0,
      fill: ink.fill, opacity: ink.opacity, src: 'signatures',
    });

    if (s.role) {
      const wCargo = larguraDe(s.role, 'inter-400', 6);
      out.push({
        k: 'glyphs', text: s.role, font: 'inter-400', size: 6,
        x: q(cx - wCargo / 2), y: q(cy + 17), charSpacing: 0,
        fill: palette.muted, opacity: q(ink.opacity * 0.85), src: 'signatures',
      });
    }
  });
}

// ─── Orquestracao ───────────────────────────────────────────────────────────

export const PAGE_A4_PAISAGEM: PageSize = { width: 841.89, height: 595.28 };

export function resolve(
  snapshot: CertificateSnapshot,
  manifest: AssetManifest,
): ResolvedCertificate {
  const out = new Saida();
  const page = PAGE_A4_PAISAGEM;
  const usados = new Set<AssetId>();

  const { config: cfg, diagnostics: diagsConfig } = normalizeConfig(snapshot.configRaw);
  for (const d of diagsConfig) out.diag(d.code, d.severity, d.message, d.element);

  const tz = snapshot.timeZone;
  if (!offsetConhecido(tz)) {
    out.diag('VALUE_CLAMPED', 'warn',
      `Fuso horário "${tz}" não é conhecido; datas saem em UTC.`);
  }

  const palette = buildPalette(cfg.textColor);

  // Fundo primeiro: tudo desenha por cima.
  const fundo = manifest.assets.find((a) => a.role === 'background');
  if (fundo) {
    out.push({
      k: 'image', x: 0, y: 0, w: page.width, h: page.height,
      asset: fundo.id, opacity: 1, src: 'background',
    });
    usados.add(fundo.id);
  } else {
    out.push({
      k: 'rect', x: 0, y: 0, w: page.width, h: page.height,
      fill: palette.paper, opacity: 1, src: 'background',
    });
    if (cfg.backgroundUrl) {
      out.diag('ASSET_MISSING', 'error',
        'A imagem de fundo do certificado não está disponível.');
    }
  }

  for (const id of ELEMENT_ORDER) {
    const el = cfg.elements[id];
    if (!el.enabled) continue;
    if (!permitidoPorFields(id, cfg)) {
      out.diag('FIELD_GATED', 'info',
        `"${id}" está ligado no layout, mas o campo correspondente está ` +
          'desmarcado nas opções do certificado. O campo vence: não será desenhado.',
        id);
      continue;
    }

    const antes = out.primitivas.length;

    switch (id) {
      case 'titleDivider':
        desenharDivisor(out, el, palette, page);
        break;
      case 'nameUnderline':
        desenharSublinhado(out, el, palette, page);
        break;
      case 'metadataRow':
        desenharDados(out, el, cfg, snapshot.data, tz, palette, page);
        break;
      case 'signatures':
        desenharAssinaturas(out, el, cfg, manifest, palette, page, usados);
        break;
      default: {
        const texto = textoDe(id, cfg, snapshot.data, tz);
        if (!texto) {
          out.diag('ELEMENT_EMPTY', 'info',
            `"${id}" está ligado mas não tem conteúdo para mostrar.`, id);
          break;
        }
        desenharTexto(out, id, el, texto, resolveInk(el.style, palette), page);
      }
    }

    if (out.primitivas.length === antes && !out.jaAvisou('ELEMENT_EMPTY', id)) {
      // Rede de seguranca: elemento ligado que nao gerou nada. Se um ramo
      // acima esquecer de emitir diagnostico, este pega.
      //
      // Quando o ramo JA avisou, calar: a mensagem especifica ("nenhum
      // signatario foi cadastrado") diz o que fazer, e a generica so repete o
      // sintoma. Dois avisos para um problema fazem o produtor procurar dois.
      out.diag('ELEMENT_EMPTY', 'warn',
        `"${id}" está ligado mas não desenhou nada no certificado.`, id);
    }
  }

  return {
    page,
    primitives: out.primitivas,
    diagnostics: out.diagnosticos(),
    assetsUsed: [...usados].sort(),
    pipelineVersion: snapshot.pipeline,
    issuedAt: snapshot.issuedAt,
  };
}
